import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import FlightIcon from '@mui/icons-material/Flight';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WorkIcon from '@mui/icons-material/Work';
import useScrollToTop from '../../hooks/useScrollToTop';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`portugal-tabpanel-${index}`}
      aria-labelledby={`portugal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const Portugal: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [selectedTab, setSelectedTab] = useState(0);
  useScrollToTop();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const fundBenefits = [
    { icon: <FlightIcon />, text: t('citizenship.portugal.fund.benefits.travel') },
    { icon: <WorkIcon />, text: t('citizenship.portugal.fund.benefits.work') },
    { icon: <SchoolIcon />, text: t('citizenship.portugal.fund.benefits.education') },
    { icon: <BusinessIcon />, text: t('citizenship.portugal.fund.benefits.business') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.portugal.fund.benefits.family') },
    { icon: <CheckCircleIcon />, text: t('citizenship.portugal.fund.benefits.quality') },
  ];

  const realEstateBenefits = [
    { icon: <FlightIcon />, text: t('citizenship.portugal.realEstate.benefits.travel') },
    { icon: <WorkIcon />, text: t('citizenship.portugal.realEstate.benefits.work') },
    { icon: <SchoolIcon />, text: t('citizenship.portugal.realEstate.benefits.education') },
    { icon: <BusinessIcon />, text: t('citizenship.portugal.realEstate.benefits.business') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.portugal.realEstate.benefits.family') },
    { icon: <CheckCircleIcon />, text: t('citizenship.portugal.realEstate.benefits.quality') },
  ];

  const fundRequirements = [
    t('citizenship.portugal.fund.requirements.investment'),
    t('citizenship.portugal.fund.requirements.criminal'),
    t('citizenship.portugal.fund.requirements.passport'),
    t('citizenship.portugal.fund.requirements.financial'),
    t('citizenship.portugal.fund.requirements.fee'),
    t('citizenship.portugal.fund.requirements.insurance'),
  ];

  const realEstateRequirements = [
    t('citizenship.portugal.realEstate.requirements.investment'),
    t('citizenship.portugal.realEstate.requirements.criminal'),
    t('citizenship.portugal.realEstate.requirements.passport'),
    t('citizenship.portugal.realEstate.requirements.financial'),
    t('citizenship.portugal.realEstate.requirements.fee'),
    t('citizenship.portugal.realEstate.requirements.insurance'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#2e7d32',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('citizenship.portugal.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('citizenship.portugal.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Tabs */}
      <Container sx={{ py: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                fontWeight: 'medium',
                minWidth: 200,
              },
            }}
          >
            <Tab label={t('citizenship.portugal.tabs.fund')} />
            <Tab label={t('citizenship.portugal.tabs.realEstate')} />
          </Tabs>
        </Box>
      </Container>

      {/* Fund Program */}
      <TabPanel value={selectedTab} index={0}>
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.portugal.fund.overview.title')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.portugal.fund.overview.description1')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.portugal.fund.overview.description2')}
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Fund Benefits */}
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.portugal.fund.benefits.title')}
                </Typography>
                <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center">
                  {fundBenefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ color: 'primary.main', mr: 2 }}>
                                {benefit.icon}
                              </Box>
                              <Typography variant="h6" component="h3">
                                {benefit.text}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Fund Requirements */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {t('citizenship.portugal.fund.requirements.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('citizenship.portugal.fund.requirements.investmentTitle')}
                    </Typography>
                    <List>
                      {fundRequirements.map((requirement, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={requirement} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('citizenship.portugal.fund.timeline.title')}
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.fund.timeline.initial.title')}
                          secondary={t('citizenship.portugal.fund.timeline.initial.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.fund.timeline.residence.title')}
                          secondary={t('citizenship.portugal.fund.timeline.residence.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.fund.timeline.permanent.title')}
                          secondary={t('citizenship.portugal.fund.timeline.permanent.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.fund.timeline.citizenship.title')}
                          secondary={t('citizenship.portugal.fund.timeline.citizenship.description')}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </TabPanel>

      {/* Real Estate Program */}
      <TabPanel value={selectedTab} index={1}>
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.portugal.realEstate.overview.title')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.portugal.realEstate.overview.description1')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.portugal.realEstate.overview.description2')}
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Real Estate Benefits */}
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.portugal.realEstate.benefits.title')}
                </Typography>
                <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center">
                  {realEstateBenefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ color: 'primary.main', mr: 2 }}>
                                {benefit.icon}
                              </Box>
                              <Typography variant="h6" component="h3">
                                {benefit.text}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Real Estate Requirements */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {t('citizenship.portugal.realEstate.requirements.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('citizenship.portugal.realEstate.requirements.title')}
                    </Typography>
                    <List>
                      {realEstateRequirements.map((requirement, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={requirement} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('citizenship.portugal.realEstate.timeline.title')}
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.realEstate.timeline.initial.title')}
                          secondary={t('citizenship.portugal.realEstate.timeline.initial.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.realEstate.timeline.residence.title')}
                          secondary={t('citizenship.portugal.realEstate.timeline.residence.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.realEstate.timeline.permanent.title')}
                          secondary={t('citizenship.portugal.realEstate.timeline.permanent.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.portugal.realEstate.timeline.citizenship.title')}
                          secondary={t('citizenship.portugal.realEstate.timeline.citizenship.description')}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </TabPanel>
    </Box>
  );
};

export default Portugal;
